<template>
  <div class="exception-criteria-editor">
    <!-- Industry Presets -->
    <div class="ece-section ece-presets-section">
      <div class="ece-section-label">Quick Start</div>
      <div class="ece-presets-grid">
        <div v-for="preset in allPresets" :key="preset.id" class="ece-preset-chip-wrap">
          <button class="ece-preset-chip" @click="addPresetToEditor(preset)">
            <i :class="preset.icon" class="ece-preset-icon"></i>
            <span>{{ preset.name }}</span>
          </button>
          <button v-if="preset.id && preset.id.startsWith && preset.id.startsWith('custom-')" class="ece-preset-delete" @click.stop="$emit('delete-preset', preset)" title="Delete preset">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Combine Mode + Description (merged into one compact row) -->
    <div class="ece-combine-row">
      <span class="ece-combine-text">Highlight days matching</span>
      <div class="ece-combine-toggle">
        <button class="ece-toggle-btn" :class="{ active: internalCombineMode === 'OR' }" @click="internalCombineMode = 'OR'">
          Any
        </button>
        <button class="ece-toggle-btn" :class="{ active: internalCombineMode === 'AND' }" @click="internalCombineMode = 'AND'">
          All
        </button>
      </div>
      <span class="ece-combine-text">of the criteria below</span>
    </div>

    <!-- Validation Error -->
    <div v-if="editorError" class="ece-error" role="alert">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span>{{ editorError }}</span>
    </div>

    <!-- Criteria Rows -->
    <div class="ece-criteria-list" :class="{ 'ece-criteria-list--empty': editingCriteria.length === 0 }">
      <div v-if="editingCriteria.length === 0" class="ece-criteria-empty">
        <i class="bi bi-funnel"></i>
        <span>No criteria yet — add one below or pick a preset above.</span>
      </div>

      <div v-for="(c, i) in editingCriteria" :key="i"
        :ref="'criterionRow-' + i"
        class="ece-criterion-row"
        :class="{ 'ece-criterion-row--invalid': c._invalid, 'ece-criterion-row--focused': c._focused }">

        <!-- Row number + color indicator -->
        <div class="ece-criterion-color" :class="'ece-color--' + c.color"></div>

        <div class="ece-criterion-fields">
          <div class="ece-field ece-field--category">
            <select class="form-select form-select-sm" :class="{ 'is-invalid': c._invalid && !c.field }" v-model="c.fieldKey" @change="onFieldChange(c)">
              <option value="">Select field...</option>
              <optgroup v-for="grp in criteriaOptionGroups" :key="grp.label" :label="grp.label">
                <option v-for="opt in grp.options" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
              </optgroup>
            </select>
          </div>
          <div class="ece-field ece-field--operator">
            <select class="form-select form-select-sm" v-model="c.operator">
              <option value="gt">&gt; Greater than</option>
              <option value="gte">&ge; At least</option>
              <option value="lt">&lt; Less than</option>
              <option value="lte">&le; At most</option>
              <option value="between">Between</option>
              <option value="contains">Contains</option>
              <option value="present">Is present</option>
            </select>
          </div>
          <div class="ece-field ece-field--value" :style="{ visibility: c.operator !== 'present' && c.operator !== 'notpresent' ? 'visible' : 'hidden' }">
            <div class="ece-value-wrap">
              <input
                type="text"
                class="form-control form-control-sm ece-value-input"
                :class="{ 'is-invalid': c._invalid && c.field && c.operator !== 'present' && c.value === '' }"
                v-model="c.value"
                :placeholder="c.operator === 'between' ? 'Min' : 'Value'"
                @input="c._invalid = false; editorError = ''"
              >
              <button v-if="getValueSuggestions(c).length" :ref="'valTrigger-' + i" class="ece-value-trigger" @click.stop="toggleValuePicker(i)" title="Quick values" tabindex="-1">
                <i class="bi bi-chevron-down"></i>
              </button>
              <div v-if="openValuePicker === i" class="ece-value-dropdown" :style="valuePickerStyle">
                <div v-for="sg in getValueSuggestions(c)" :key="sg.group" class="ece-value-group">
                  <div class="ece-value-group-label">{{ sg.group }}</div>
                  <button v-for="s in sg.items" :key="s.value" class="ece-value-option" :class="{ 'ece-value-option--active': String(c.value) === String(s.value) }" @click.stop="pickValue(c, s.value)">
                    <span class="ece-value-option-val">{{ s.value }}</span>
                    <span class="ece-value-option-label">{{ s.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="c.operator === 'between'" class="ece-field ece-field--value">
            <div class="ece-value-wrap">
              <input type="text" class="form-control form-control-sm ece-value-input" v-model="c.value2" placeholder="Max">
              <button v-if="getValueSuggestions(c).length" :ref="'valTrigger-' + (i + 1000)" class="ece-value-trigger" @click.stop="toggleValuePicker(i + 1000)" title="Quick values" tabindex="-1">
                <i class="bi bi-chevron-down"></i>
              </button>
              <div v-if="openValuePicker === i + 1000" class="ece-value-dropdown" :style="valuePickerStyle">
                <div v-for="sg in getValueSuggestions(c)" :key="sg.group" class="ece-value-group">
                  <div class="ece-value-group-label">{{ sg.group }}</div>
                  <button v-for="s in sg.items" :key="s.value" class="ece-value-option" :class="{ 'ece-value-option--active': String(c.value2) === String(s.value) }" @click.stop="pickValue2(c, s.value)">
                    <span class="ece-value-option-val">{{ s.value }}</span>
                    <span class="ece-value-option-label">{{ s.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="ece-field ece-field--color">
            <div class="ece-color-picker">
              <button class="ece-color-trigger" @click.stop="toggleColorPicker(i)" :title="colorMap[c.color]?.label || c.color">
                <span class="ece-color-dot" :style="{ background: colorMap[c.color]?.hex || '#94a3b8' }"></span>
                <span class="ece-color-name">{{ colorMap[c.color]?.label || c.color }}</span>
                <i class="bi bi-chevron-down ece-color-caret"></i>
              </button>
              <div v-if="openColorPicker === i" class="ece-color-dropdown">
                <button v-for="(col, key) in colorMap" :key="key"
                  class="ece-color-option" :class="{ 'ece-color-option--active': c.color === key }"
                  @click.stop="c.color = key; openColorPicker = null">
                  <span class="ece-color-dot" :style="{ background: col.hex }"></span>
                  <span>{{ col.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Label toggle + delete -->
        <div class="ece-criterion-actions">
          <button class="ece-icon-btn" :class="{ 'ece-icon-btn--active': c._showLabel || c.name }" @click="toggleLabel(c, i)" title="Custom label">
            <i class="bi bi-tag"></i>
          </button>
          <button class="ece-icon-btn ece-icon-btn--danger" @click="removeCriterion(i)" title="Remove">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Collapsible custom label -->
        <div v-if="c._showLabel" class="ece-criterion-label">
          <input type="text" class="form-control form-control-sm"
            :ref="'labelInput-' + i"
            v-model="c.name"
            placeholder="Custom label (e.g. &quot;Frost risk&quot;)"
            @blur="onLabelBlur(c)"
            @keydown.enter="onLabelBlur(c)">
        </div>
        <div v-else-if="c.name" class="ece-criterion-label-text" @click="toggleLabel(c, i)">
          <i class="bi bi-tag-fill ece-label-icon"></i>{{ c.name }}
        </div>
      </div>
    </div>

    <!-- Add / Clear Buttons -->
    <div class="ece-add-row">
      <button class="ece-add-btn" @click="addCriterion">
        <i class="bi bi-plus-circle me-1"></i>Add Criterion
      </button>
      <button v-if="editingCriteria.length > 0" class="ece-clear-btn" @click="clearAll">
        <i class="bi bi-trash3 me-1"></i>Clear All
      </button>
    </div>

    <!-- Notification Trigger (only shown when showNotification is true) -->
    <div v-if="showNotification" class="ece-section">
      <div class="ece-section-header">
        <i class="bi bi-bell me-2"></i>
        <span class="ece-section-title">Notification Trigger</span>
      </div>
      <div class="ece-section-body">
        <select class="form-select form-select-sm" v-model="internalNotificationTrigger">
          <option value="any_exception">Notify on any exception</option>
          <option value="danger_only">Notify only on danger-severity exceptions</option>
          <option value="always">Always notify (even with no exceptions)</option>
        </select>
        <div class="ece-hint mt-1">
          <i class="bi bi-info-circle"></i>Controls when email/webhook notifications are sent after a run.
        </div>
      </div>
    </div>

    <!-- Save Preset / Save Actions -->
    <div class="ece-footer">
      <div class="ece-save-preset">
        <input type="text" class="form-control form-control-sm" v-model="newPresetName" placeholder="Save as preset...">
        <button class="ece-icon-btn" @click="saveAsPreset" :disabled="!newPresetName || editingCriteria.length === 0" title="Save current criteria as a reusable preset">
          <i class="bi bi-bookmark-plus"></i>
        </button>
      </div>
      <div class="ece-footer-actions">
        <button v-if="showCancel" class="btn btn-sm btn-light" @click="$emit('cancel')">Cancel</button>
        <button class="btn btn-sm btn-primary" @click="save">
          <i class="bi bi-check2 me-1"></i>Save Criteria
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { CRITERIA_OPTIONS, createCriterion, getDefaultColorForField } from './exceptionCriteriaPresets.js';

export default {
  name: 'ExceptionCriteriaEditor',

  props: {
    /** Array of criterion objects (the real model) */
    modelValue: {
      type: Array,
      default: () => []
    },
    /** Combine mode: 'AND' or 'OR' */
    combineMode: {
      type: String,
      default: 'OR'
    },
    /** Business hours configuration */
    businessHours: {
      type: Object,
      default: () => ({ enabled: false, start: '08:00', end: '17:00' })
    },
    /** Unit group for unit-aware preset values */
    unitGroup: {
      type: String,
      default: 'us'
    },
    /** Whether to show notification trigger selector */
    showNotification: {
      type: Boolean,
      default: false
    },
    /** Notification trigger value */
    notificationTrigger: {
      type: String,
      default: 'any_exception'
    },
    /** Whether to show the Cancel button */
    showCancel: {
      type: Boolean,
      default: true
    },
    /** Additional presets to append to the built-in list */
    customPresets: {
      type: Array,
      default: () => []
    },
    /** ID of a criterion to scroll into view and highlight when the editor opens */
    focusCriterionId: {
      type: String,
      default: null
    }
  },

  emits: ['update:modelValue', 'update:combineMode', 'update:businessHours', 'update:notificationTrigger', 'save', 'cancel', 'save-preset', 'delete-preset'],

  data() {
    return {
      editingCriteria: [],
      editorError: '',
      newPresetName: '',
      openColorPicker: null,
      openValuePicker: null,
      valuePickerStyle: {},
      recentValues: {},   // { fieldId: [v1, v2, v3] }
      internalCombineMode: this.combineMode,
      internalBusinessHours: { ...this.businessHours },
      internalNotificationTrigger: this.notificationTrigger,
      colorMap: {
        warning: { label: 'Yellow', hex: '#ffc107' },
        danger:  { label: 'Red',    hex: '#ef4444' },
        info:    { label: 'Blue',   hex: '#3b82f6' },
        purple:  { label: 'Purple', hex: '#8b5cf6' },
        orange:  { label: 'Orange', hex: '#f97316' },
        teal:    { label: 'Teal',   hex: '#14b8a6' },
        cyan:    { label: 'Cyan',   hex: '#06b6d4' },
        pink:    { label: 'Pink',   hex: '#ec4899' },
        indigo:  { label: 'Indigo', hex: '#6366f1' },
        lime:    { label: 'Lime',   hex: '#84cc16' },
        success: { label: 'Green',  hex: '#22c55e' }
      },
      // Built-in presets
      builtInPresets: [
        {
          id: 'general',
          name: 'General Monitoring',
          icon: 'bi bi-shield-check',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'windgust', op: 'gt', metricValue: 40, usValue: 25, name: 'High wind', color: 'purple' }
          ]
        },
        {
          id: 'construction',
          name: 'Construction Work',
          icon: 'bi bi-cone-striped',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Freezing risk', color: 'teal' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'Dangerous wind', color: 'purple' },
            { field: 'snow', op: 'gt', value: 0, name: 'Snow expected', color: 'cyan' }
          ]
        },
        {
          id: 'shipping',
          name: 'Temperature-Sensitive Shipping',
          icon: 'bi bi-truck',
          rules: [
            { field: 'tempmax', op: 'gt', metricValue: 30, usValue: 86, name: 'Too hot for cargo', color: 'danger' },
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Freezing risk', color: 'teal' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather risk', color: 'danger' }
          ]
        },
        {
          id: 'agriculture',
          name: 'Farming / Agriculture',
          icon: 'bi bi-flower1',
          rules: [
            { field: 'tempmin', op: 'lt', metricValue: 2, usValue: 35, name: 'Frost risk', color: 'teal' },
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'windgust', op: 'gt', metricValue: 40, usValue: 25, name: 'High wind', color: 'purple' }
          ]
        },
        {
          id: 'aviation',
          name: 'Aviation / Flying',
          icon: 'bi bi-airplane',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Precipitation likely', color: 'info' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'High wind', color: 'purple' },
            { field: 'snow', op: 'gt', value: 0, name: 'Snow/ice risk', color: 'cyan' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather risk', color: 'danger' }
          ]
        },
        {
          id: 'outdoor-leisure',
          name: 'Outdoor Leisure',
          icon: 'bi bi-tree',
          rules: [
            { field: 'precipprob', op: 'gt', value: 50, name: 'Rain likely', color: 'info' },
            { field: 'tempmax', op: 'gt', metricValue: 37, usValue: 99, name: 'Extreme heat', color: 'danger' },
            { field: 'tempmin', op: 'lt', metricValue: -5, usValue: 23, name: 'Very cold', color: 'teal' },
            { field: 'windgust', op: 'gt', metricValue: 50, usValue: 30, name: 'High wind', color: 'purple' },
            { field: 'severerisk', op: 'gt', value: 30, name: 'Severe weather', color: 'danger' }
          ]
        }
      ]
    };
  },

  computed: {
    criteriaOptionGroups() {
      const groupMap = {};
      for (const opt of CRITERIA_OPTIONS) {
        if (!groupMap[opt.group]) groupMap[opt.group] = { label: opt.group, options: [] };
        groupMap[opt.group].options.push(opt);
      }
      return Object.values(groupMap);
    },

    allPresets() {
      return [...this.builtInPresets, ...this.customPresets];
    },

    combineModeLabel() {
      return this.internalCombineMode === 'AND' ? 'all criteria' : 'any criterion';
    }
  },

  watch: {
    modelValue: {
      handler(newVal) {
        this.loadFromModel(newVal);
      },
      immediate: true
    },
    combineMode(val) {
      this.internalCombineMode = val;
    },
    businessHours: {
      handler(val) {
        this.internalBusinessHours = { ...val };
      },
      deep: true
    },
    notificationTrigger(val) {
      this.internalNotificationTrigger = val;
    },
    internalCombineMode(val) {
      this.$emit('update:combineMode', val);
    },
    internalBusinessHours: {
      handler(val) {
        this.$emit('update:businessHours', { ...val });
      },
      deep: true
    },
    internalNotificationTrigger(val) {
      this.$emit('update:notificationTrigger', val);
    }
  },

  mounted() {
    document.addEventListener('click', this._onDocumentClick);
    this._loadRecentValues();
  },

  beforeUnmount() {
    document.removeEventListener('click', this._onDocumentClick);
  },

  methods: {
    /** Populate editing rows from the model criteria */
    loadFromModel(criteria) {
      this.editingCriteria = (criteria || []).map(c => {
        const gran = c.granularity || 'daily';
        const fieldKey = c.field ? `${c.field}:${gran}` : '';
        return {
          _id: c.id || null,
          fieldKey,
          field: c.field,
          granularity: gran,
          operator: c.operator,
          value: Array.isArray(c.value) ? String(c.value[0]) : String(c.value ?? ''),
          value2: Array.isArray(c.value) ? String(c.value[1]) : '',
          color: c.color || 'warning',
          name: c.name || '',
          _quickId: c._quickId || null,
          _invalid: false,
          _showLabel: false,
          _focused: false
        };
      });
      this.editorError = '';

      // If a focusCriterionId was provided, scroll to and highlight the matching row
      if (this.focusCriterionId) {
        this.$nextTick(() => {
          const idx = this.editingCriteria.findIndex(c => c._id === this.focusCriterionId);
          if (idx >= 0) {
            this.editingCriteria[idx]._focused = true;
            const refKey = 'criterionRow-' + idx;
            const el = this.$refs[refKey];
            const target = Array.isArray(el) ? el[0] : el;
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            // Remove highlight after animation
            setTimeout(() => {
              if (this.editingCriteria[idx]) this.editingCriteria[idx]._focused = false;
            }, 1500);
          }
        });
      }
    },

    /** Add a blank criterion row */
    addCriterion() {
      this.editingCriteria.push({
        fieldKey: '', field: '', granularity: 'daily',
        operator: 'gt', value: '', value2: '',
        color: 'warning', name: '', _quickId: null, _invalid: false, _showLabel: false
      });
    },

    /** Remove criterion at index */
    removeCriterion(index) {
      this.editingCriteria.splice(index, 1);
    },

    /** Clear all criteria */
    clearAll() {
      this.editingCriteria = [];
    },

    /** Toggle label editor — opens the input and focuses it */
    toggleLabel(c, index) {
      c._showLabel = !c._showLabel;
      if (c._showLabel) {
        this.$nextTick(() => {
          const ref = this.$refs['labelInput-' + index];
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el) el.focus();
        });
      }
    },

    /** When the label input loses focus, close it if empty */
    onLabelBlur(c) {
      if (!c.name) {
        c._showLabel = false;
      } else {
        c._showLabel = false;
      }
    },

    /** Toggle color picker for row index */
    toggleColorPicker(index) {
      this.openColorPicker = this.openColorPicker === index ? null : index;
    },

    /** Close color picker / value picker on outside click */
    _onDocumentClick(e) {
      if (this.openColorPicker !== null && !e.target.closest('.ece-color-picker')) {
        this.openColorPicker = null;
      }
      if (this.openValuePicker !== null && !e.target.closest('.ece-value-wrap')) {
        this.openValuePicker = null;
      }
    },

    // ── Value quick-pick helpers ──

    /** Toggle value picker dropdown for a criterion row */
    toggleValuePicker(index) {
      if (this.openValuePicker === index) {
        this.openValuePicker = null;
        return;
      }
      this.openColorPicker = null;
      this.openValuePicker = index;
      // Position the dropdown using fixed coords from the trigger button
      this.$nextTick(() => {
        const refKey = 'valTrigger-' + index;
        const el = this.$refs[refKey];
        const trigger = Array.isArray(el) ? el[0] : el;
        if (trigger) {
          const rect = trigger.getBoundingClientRect();
          this.valuePickerStyle = {
            position: 'fixed',
            top: rect.bottom + 2 + 'px',
            left: rect.left + 'px',
            zIndex: 9999
          };
        }
      });
    },

    /** Pick a value from the dropdown */
    pickValue(criterion, value) {
      criterion.value = String(value);
      criterion._invalid = false;
      this.editorError = '';
      this.openValuePicker = null;
    },

    /** Pick a value2 (max for between) from the dropdown */
    pickValue2(criterion, value) {
      criterion.value2 = String(value);
      this.openValuePicker = null;
    },

    /** Get preset + recent value suggestions for a criterion */
    getValueSuggestions(criterion) {
      if (!criterion.field) return [];
      const field = criterion.field;
      const isMetric = this.unitGroup === 'metric';
      const groups = [];

      // 1. Built-in presets for this field
      const presets = this._getPresetValues(field, isMetric);
      if (presets.length) {
        groups.push({ group: 'Common', items: presets });
      }

      // 2. Recent custom values
      const recent = (this.recentValues[field] || []).filter(v => {
        // exclude any that already appear in presets
        return !presets.some(p => String(p.value) === String(v));
      }).map(v => ({ value: v, label: '' }));
      if (recent.length) {
        groups.push({ group: 'Recent', items: recent });
      }

      return groups;
    },

    /** Return built-in quick-pick values for a field */
    _getPresetValues(field, isMetric) {
      const V = (us, metric, label) => ({ value: isMetric ? metric : us, label });
      switch (field) {
        // Temperature fields
        case 'tempmax': case 'tempmin': case 'temp':
          return [
            V(32, 0, 'Freezing'),
            V(50, 10, 'Cool'),
            V(68, 20, 'Comfortable'),
            V(86, 30, 'Hot'),
            V(95, 35, 'Very hot'),
            V(104, 40, 'Extreme heat'),
          ];
        case 'feelslikemax': case 'feelslikemin': case 'feelslike':
          return [
            V(32, 0, 'Freezing'),
            V(50, 10, 'Cool'),
            V(86, 30, 'Hot'),
            V(105, 41, 'Dangerous'),
          ];
        // Wind
        case 'windspeed': case 'windgust':
          return [
            V(13, 20, 'Moderate breeze'),
            V(25, 40, 'Strong breeze'),
            V(39, 63, 'Near gale'),
            V(47, 76, 'Gale'),
            V(64, 103, 'Storm'),
          ];
        // Precipitation
        case 'precip':
          return [
            V(0.1, 2.5, 'Light'),
            V(0.3, 7.6, 'Moderate'),
            V(0.5, 12.7, 'Heavy'),
            V(1.0, 25.4, 'Very heavy'),
          ];
        case 'precipprob':
          return [
            { value: 25, label: 'Low chance' },
            { value: 50, label: 'Likely' },
            { value: 75, label: 'Very likely' },
            { value: 90, label: 'Almost certain' },
          ];
        // Snow
        case 'snow':
          return [
            V(0.5, 1, 'Dusting'),
            V(2, 5, 'Light'),
            V(6, 15, 'Moderate'),
            V(12, 30, 'Heavy'),
          ];
        case 'snowdepth':
          return [
            V(1, 2.5, 'Light cover'),
            V(6, 15, 'Moderate'),
            V(12, 30, 'Deep'),
          ];
        // Humidity
        case 'humidity':
          return [
            { value: 30, label: 'Dry' },
            { value: 60, label: 'Comfortable' },
            { value: 80, label: 'Humid' },
            { value: 90, label: 'Very humid' },
          ];
        // UV Index
        case 'uvindex':
          return [
            { value: 3, label: 'Moderate' },
            { value: 6, label: 'High' },
            { value: 8, label: 'Very high' },
            { value: 11, label: 'Extreme' },
          ];
        // Visibility
        case 'visibility':
          return [
            V(0.6, 1, 'Fog'),
            V(3, 5, 'Poor'),
            V(6, 10, 'Moderate'),
          ];
        // Cloud cover
        case 'cloudcover':
          return [
            { value: 25, label: 'Mostly clear' },
            { value: 50, label: 'Partly cloudy' },
            { value: 75, label: 'Mostly cloudy' },
            { value: 90, label: 'Overcast' },
          ];
        // Pressure
        case 'pressure':
          return [
            V(29.5, 1000, 'Low pressure'),
            V(30.0, 1013, 'Standard'),
            V(30.5, 1033, 'High pressure'),
          ];
        // Severe risk
        case 'severerisk':
          return [
            { value: 30, label: 'Moderate' },
            { value: 50, label: 'Elevated' },
            { value: 70, label: 'High' },
          ];
        // Dew point
        case 'dew':
          return [
            V(32, 0, 'Freezing'),
            V(55, 13, 'Comfortable'),
            V(65, 18, 'Muggy'),
            V(70, 21, 'Oppressive'),
          ];
        // Solar
        case 'solarradiation':
          return [
            { value: 200, label: 'Low' },
            { value: 500, label: 'Moderate' },
            { value: 800, label: 'High' },
          ];
        case 'solarenergy':
          return [
            { value: 5, label: 'Low' },
            { value: 15, label: 'Moderate' },
            { value: 25, label: 'High' },
          ];
        default:
          return [];
      }
    },

    /** Record a custom value into recent values for a field */
    _recordRecentValue(field, value) {
      if (!field || value === '' || value == null) return;
      const v = String(value);
      // Don't record if it matches a built-in preset
      const isMetric = this.unitGroup === 'metric';
      const presets = this._getPresetValues(field, isMetric);
      if (presets.some(p => String(p.value) === v)) return;

      if (!this.recentValues[field]) this.recentValues[field] = [];
      const arr = this.recentValues[field];
      // Remove if already present (will re-add at front)
      const idx = arr.indexOf(v);
      if (idx !== -1) arr.splice(idx, 1);
      arr.unshift(v);
      // Keep only 3
      if (arr.length > 3) arr.length = 3;
      this._saveRecentValues();
    },

    /** Load recent values from localStorage */
    _loadRecentValues() {
      try {
        const raw = localStorage.getItem('ece_recentValues');
        if (raw) this.recentValues = JSON.parse(raw);
      } catch (e) { /* ignore */ }
    },

    /** Persist recent values to localStorage */
    _saveRecentValues() {
      try {
        localStorage.setItem('ece_recentValues', JSON.stringify(this.recentValues));
      } catch (e) { /* ignore */ }
    },

    /** Handle field dropdown change — sync field/granularity and assign default color */
    onFieldChange(criterion) {
      if (criterion.fieldKey) {
        const [field, granularity] = criterion.fieldKey.split(':');
        criterion.field = field;
        criterion.granularity = granularity || 'daily';
        criterion.color = getDefaultColorForField(field);
      } else {
        criterion.field = '';
        criterion.granularity = 'daily';
      }
      criterion._invalid = false;
      this.editorError = '';
    },

    /** Validate and emit saved criteria */
    save() {
      this.editorError = '';
      let invalidCount = 0;
      for (const c of this.editingCriteria) {
        const needsValue = c.operator !== 'present' && c.operator !== 'notpresent';
        if (!c.field || (needsValue && c.value === '')) {
          c._invalid = true;
          invalidCount++;
        }
      }
      if (invalidCount) {
        this.editorError = `${invalidCount} criterion${invalidCount > 1 ? 'a are' : ' is'} incomplete — please select a field and enter a value for the highlighted rows.`;
        this.$nextTick(() => {
          const el = this.$el.querySelector('.criterion-editor-card--invalid');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }

      // Record custom values for future quick-pick
      for (const c of this.editingCriteria) {
        if (c.field && c.value !== '') {
          this._recordRecentValue(c.field, c.value);
          if (c.operator === 'between' && c.value2 !== '') {
            this._recordRecentValue(c.field, c.value2);
          }
        }
      }

      const criteria = this.editingCriteria
        .filter(c => c.field && (c.operator === 'present' || c.value !== ''))
        .map(c => {
          let value;
          if (c.operator === 'between') {
            value = [parseFloat(c.value), parseFloat(c.value2)];
          } else if (c.operator === 'present') {
            value = true;
          } else if (c.operator === 'contains') {
            value = c.value;
          } else {
            value = parseFloat(c.value);
          }
          const criterion = createCriterion(
            c.field, c.operator, value, c.granularity || 'daily', c.name || null, { color: c.color }
          );
          if (c._quickId) criterion._quickId = c._quickId;
          return criterion;
        });

      this.$emit('update:modelValue', criteria);
      this.$emit('save', {
        criteria,
        combineMode: this.internalCombineMode,
        businessHours: { ...this.internalBusinessHours },
        notificationTrigger: this.internalNotificationTrigger
      });
    },

    /** Convert a preset rule to an editing row */
    _presetRuleToEditorRow(r) {
      const value = r.metricValue !== undefined
        ? (this.unitGroup === 'us' ? r.usValue : r.metricValue)
        : r.value;
      const gran = r.granularity || 'daily';
      const field = r.field;
      return {
        fieldKey: field ? `${field}:${gran}` : '',
        field,
        granularity: gran,
        operator: r.op || r.operator || 'gt',
        value: value != null ? String(value) : '',
        value2: '',
        color: r.color || 'warning',
        name: r.name || '',
        _quickId: null,
        _showLabel: !!(r.name)
      };
    },

    /** Add a preset's rules to the editor, deduplicating by fieldKey */
    addPresetToEditor(preset) {
      const rules = preset.rules || preset.criteria || [];
      const newRows = rules.map(r => this._presetRuleToEditorRow(r));
      const incomingFields = new Set(newRows.map(r => r.fieldKey));
      this.editingCriteria = [
        ...this.editingCriteria.filter(c => !incomingFields.has(c.fieldKey)),
        ...newRows
      ];
    },

    /** Save current editing criteria as a reusable custom preset */
    saveAsPreset() {
      const validCriteria = this.editingCriteria.filter(c => c.field);
      if (!this.newPresetName || validCriteria.length === 0) return;

      const criteria = validCriteria.map(c => {
        let value;
        if (c.operator === 'between') value = [parseFloat(c.value), parseFloat(c.value2)];
        else if (c.operator === 'present') value = true;
        else if (c.operator === 'contains') value = c.value;
        else value = parseFloat(c.value);
        return {
          field: c.field, operator: c.operator, value,
          granularity: c.granularity, name: c.name, color: c.color
        };
      });

      const newPreset = {
        id: `custom-${Date.now()}`,
        name: this.newPresetName,
        icon: 'bi bi-bookmark-star',
        criteria
      };

      this.$emit('save-preset', newPreset);
      this.newPresetName = '';
    }
  }
};
</script>

<style scoped>
/* ── Design tokens (inherits --fwt-* from parent, with fallbacks) ── */
.exception-criteria-editor {
  --ece-radius: var(--fwt-radius-sm, 8px);
  --ece-border: var(--fwt-border-light, #c5ced6);
  --ece-text: var(--fwt-text, #1e293b);
  --ece-text-muted: var(--fwt-text-muted, #586674);
  --ece-bg: var(--fwt-bg-card, #fff);
  --ece-bg-subtle: #f8f9fc;
  --ece-primary: #4361ee;
  --ece-danger: #ef4444;
  font-family: var(--fwt-font, inherit);
  color: var(--ece-text);
}

/* ── Section scaffolding ── */
.ece-section {
  border: 1px solid var(--ece-border);
  border-radius: var(--ece-radius);
  margin-bottom: 16px;
  overflow: hidden;
}
.ece-section-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ece-text-muted);
  margin-bottom: 8px;
  padding: 12px 14px 0;
}
.ece-section-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ece-text);
  cursor: pointer;
  user-select: none;
  background: var(--ece-bg-subtle);
}
.ece-section-title { flex: 1; }
.ece-section-body {
  padding: 12px 14px;
  border-top: 1px solid var(--ece-border);
}

/* ── Preset chips ── */
.ece-presets-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 14px 12px;
}
.ece-preset-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border: 1px solid var(--ece-border);
  border-radius: 20px;
  background: var(--ece-bg);
  color: var(--ece-text);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ece-preset-chip:hover {
  background: var(--ece-primary);
  border-color: var(--ece-primary);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(67, 97, 238, 0.25);
}
.ece-preset-chip:active { transform: translateY(0); }
.ece-preset-icon { font-size: 0.85rem; opacity: 0.8; }
.ece-preset-chip:hover .ece-preset-icon { opacity: 1; }

/* ── Preset chip wrapper (for delete button) ── */
.ece-preset-chip-wrap {
  position: relative;
  display: inline-flex;
}
.ece-preset-delete {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid var(--ece-border);
  background: var(--ece-bg);
  color: var(--ece-text-muted);
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s ease;
  padding: 0;
  line-height: 1;
}
.ece-preset-chip-wrap:hover .ece-preset-delete {
  opacity: 1;
}
.ece-preset-delete:hover {
  background: #fef2f2;
  border-color: var(--ece-danger);
  color: var(--ece-danger);
}

/* ── Combine mode row ── */
.ece-combine-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  margin-bottom: 12px;
  font-size: 0.8rem;
  color: var(--ece-text-muted);
}
.ece-combine-toggle {
  display: inline-flex;
  border: 1px solid var(--ece-border);
  border-radius: 6px;
  overflow: hidden;
}
.ece-toggle-btn {
  padding: 3px 12px;
  border: none;
  background: var(--ece-bg);
  color: var(--ece-text-muted);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.ece-toggle-btn + .ece-toggle-btn { border-left: 1px solid var(--ece-border); }
.ece-toggle-btn.active {
  background: var(--ece-primary);
  color: #fff;
}

/* ── Validation error ── */
.ece-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 0.8rem;
  color: var(--ece-danger);
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--ece-radius);
}

/* ── Criteria list container ── */
.ece-criteria-list {
  border: 1px solid var(--ece-border);
  border-radius: var(--ece-radius);
  margin-bottom: 12px;
}
.ece-criteria-list--empty {
  border-style: dashed;
}
.ece-criteria-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  color: var(--ece-text-muted);
  font-size: 0.82rem;
}
.ece-criteria-empty i { font-size: 1.1rem; opacity: 0.5; }

/* ── Single criterion row ── */
.ece-criterion-row {
  display: grid;
  grid-template-columns: 5px 1fr auto;
  grid-template-rows: auto auto;
  align-items: center;
  gap: 0 10px;
  padding: 10px 12px 10px 0;
  background: var(--ece-bg);
  transition: background 0.15s ease;
}
.ece-criterion-row:not(:last-child) {
  border-bottom: 1px solid var(--ece-border);
}
.ece-criterion-row:hover {
  background: var(--ece-bg-subtle);
}
.ece-criterion-row--invalid {
  background: #fff5f5 !important;
}
.ece-criterion-row--focused {
  animation: ece-focus-pulse 1.5s ease-out;
}
@keyframes ece-focus-pulse {
  0%   { background: #cfe2ff; box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.35); }
  100% { background: transparent; box-shadow: none; }
}

/* ── Color indicator bar ── */
.ece-criterion-color {
  grid-row: 1 / -1;
  width: 5px;
  height: 100%;
  border-radius: 0 3px 3px 0;
  background: #94a3b8;
  align-self: stretch;
}
.ece-color--warning  { background: #ffc107; }
.ece-color--danger   { background: #ef4444; }
.ece-color--info     { background: #3b82f6; }
.ece-color--purple   { background: #8b5cf6; }
.ece-color--orange   { background: #f97316; }
.ece-color--teal     { background: #14b8a6; }
.ece-color--cyan     { background: #06b6d4; }
.ece-color--pink     { background: #ec4899; }
.ece-color--indigo   { background: #6366f1; }
.ece-color--lime     { background: #84cc16; }
.ece-color--success  { background: #22c55e; }

/* ── Criterion fields layout ── */
.ece-criterion-fields {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
}
.ece-field--category { flex: 1 1 170px; min-width: 140px; }
.ece-field--operator { flex: 0 1 140px; min-width: 120px; }
.ece-field--value    { flex: 0 0 68px; position: relative; }
.ece-field--color    { flex: 0 1 110px; min-width: 90px; position: relative; }

/* ── Value quick-pick ── */
.ece-value-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.ece-value-input {
  padding-right: 22px !important;
}
.ece-value-trigger {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--ece-text-muted);
  font-size: 0.65rem;
  cursor: pointer;
  padding: 0;
  opacity: 0.5;
  transition: opacity 0.15s ease;
}
.ece-value-wrap:hover .ece-value-trigger,
.ece-value-trigger:focus { opacity: 1; }
.ece-value-dropdown {
  position: fixed;
  z-index: 9999;
  min-width: 170px;
  max-height: 260px;
  overflow-y: auto;
  background: var(--ece-bg);
  border: 1px solid var(--ece-border);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 4px 0;
}
.ece-value-group-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--ece-text-muted);
  padding: 6px 10px 2px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ece-value-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 10px;
  border: none;
  background: transparent;
  color: var(--ece-text);
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
}
.ece-value-option:hover { background: var(--ece-bg-subtle); }
.ece-value-option--active { background: #eef2ff; font-weight: 600; }
.ece-value-option-val {
  font-weight: 600;
  min-width: 36px;
}
.ece-value-option-label {
  color: var(--ece-text-muted);
  font-size: 0.75rem;
}

/* ── Color swatch picker ── */
.ece-color-picker {
  position: relative;
}
.ece-color-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 3px 8px;
  border: 1px solid var(--ece-border);
  border-radius: 6px;
  background: var(--ece-bg);
  color: var(--ece-text);
  font-size: 0.8rem;
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.ece-color-trigger:hover {
  border-color: #94a3b8;
}
.ece-color-trigger:focus {
  outline: none;
  border-color: var(--ece-primary);
  box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.1);
}
.ece-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.ece-color-name {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ece-color-caret {
  font-size: 0.6rem;
  opacity: 0.5;
  flex-shrink: 0;
}
.ece-color-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1060;
  min-width: 130px;
  max-height: 220px;
  overflow-y: auto;
  background: var(--ece-bg);
  border: 1px solid var(--ece-border);
  border-radius: var(--ece-radius);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 4px;
}
.ece-color-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--ece-text);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.1s ease;
  text-align: left;
}
.ece-color-option:hover {
  background: var(--ece-bg-subtle);
}
.ece-color-option--active {
  background: rgba(67, 97, 238, 0.08);
  font-weight: 600;
}
.ece-color-option .ece-color-dot {
  width: 14px;
  height: 14px;
}

/* ── Action icon buttons ── */
.ece-criterion-actions {
  display: flex;
  gap: 2px;
  align-items: center;
}
.ece-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ece-text-muted);
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.ece-icon-btn:hover { background: var(--ece-border); color: var(--ece-text); }
.ece-icon-btn--active { color: var(--ece-primary); }
.ece-icon-btn--danger:hover { background: #fef2f2; color: var(--ece-danger); }
.ece-icon-btn:disabled { opacity: 0.35; pointer-events: none; }

/* ── Collapsible label row ── */
.ece-criterion-label {
  grid-column: 2 / -1;
  padding-top: 4px;
}
.ece-criterion-label input {
  font-size: 0.78rem;
  background: var(--ece-bg-subtle);
  border-color: transparent;
}
.ece-criterion-label input:focus {
  background: var(--ece-bg);
  border-color: var(--ece-primary);
}

/* ── Clickable label text (when name is set but not editing) ── */
.ece-criterion-label-text {
  grid-column: 2 / -1;
  font-size: 0.76rem;
  color: var(--ece-text-muted);
  cursor: pointer;
  padding: 2px 0 0 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ece-criterion-label-text:hover {
  color: var(--ece-primary);
}
.ece-label-icon {
  font-size: 0.68rem;
  margin-right: 3px;
  opacity: 0.55;
}

/* ── Add / Clear row ── */
.ece-add-row {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.ece-add-btn, .ece-clear-btn {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 500;
  border-radius: 6px;
  border: 1px dashed var(--ece-border);
  background: transparent;
  color: var(--ece-text-muted);
  cursor: pointer;
  transition: all 0.15s ease;
}
.ece-add-btn:hover {
  border-color: var(--ece-primary);
  color: var(--ece-primary);
  background: rgba(67, 97, 238, 0.04);
}
.ece-clear-btn:hover {
  border-color: var(--ece-danger);
  color: var(--ece-danger);
  background: rgba(239, 68, 68, 0.04);
}

/* ── Business hours ── */
/* ── Hint text ── */
.ece-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.75rem;
  color: var(--ece-text-muted);
  line-height: 1.4;
}

/* ── Footer ── */
.ece-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--ece-border);
}
.ece-save-preset {
  display: flex;
  align-items: center;
  gap: 4px;
}
.ece-save-preset input {
  width: 150px;
  font-size: 0.78rem;
}
.ece-footer-actions {
  display: flex;
  gap: 6px;
}
.ece-footer-actions .btn { font-weight: 500; }

/* ── Form control refinements ── */
.exception-criteria-editor .form-select-sm,
.exception-criteria-editor .form-control-sm {
  font-size: 0.8rem;
  border-radius: 6px;
  border-color: var(--ece-border);
}
.exception-criteria-editor .form-select-sm:focus,
.exception-criteria-editor .form-control-sm:focus {
  border-color: var(--ece-primary);
  box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.1);
}
.exception-criteria-editor .form-check-input:checked {
  background-color: var(--ece-primary);
  border-color: var(--ece-primary);
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .ece-criterion-fields { flex-direction: column; gap: 4px; }
  .ece-field--category,
  .ece-field--operator,
  .ece-field--value,
  .ece-field--color { flex: 1 1 100%; min-width: 0; }
  .ece-combine-row { flex-wrap: wrap; }
  .ece-footer { flex-direction: column; gap: 10px; align-items: stretch; }
  .ece-footer-actions { justify-content: flex-end; }
}
</style>
